<div class="calendar">
  <header class="calendar__header">
    <div>
      <h1>Calendar</h1>
      <p>A running list of what is coming up next. Pinned items stay at the top.</p>
    </div>
    <div class="calendar__meta">
      <button mat-stroked-button color="primary" (click)="openDayPicker()">Pick a day</button>
      <button mat-flat-button color="primary" (click)="openCreate()">New entry</button>
      <span class="pill pill--soft">{{ upcomingEntries.length }} upcoming</span>
    </div>
  </header>
  <div class="calendar__meta-row" *ngIf="selectedDay">
    <span class="pill">Selected day: {{ selectedDay | date: 'EEEE L MMMM' }}</span>
  </div>

  <!-- Previous Entries -->
  <section class="calendar__list" *ngIf="pastEntries.length">
    <div class="section-title">Previous</div>
    <article class="calendar-entry" *ngFor="let entry of pastEntries" [style.opacity]="entryOpacity(entry)">
      <div class="calendar-entry__time">
        <div class="calendar-entry__date">!!!{{ asDate(entry) | date: 'EEEE' }} {{ asDate(entry) | date: 'dd MMM' }}</div>
      </div>
      <div class="calendar-entry__body">
        <div class="calendar-entry__title-row">
          <div class="calendar-entry__title">
            <h3>{{ entry.title }}</h3>
            <div class="calendar-entry__subtitle" *ngIf="entry.subtitle">{{ entry.subtitle }}</div>
          </div>
          <div class="calendar-entry__actions">
            <button mat-stroked-button color="primary" (click)="openReschedule(entry)">
              Reschedule
            </button>
            <button mat-stroked-button color="primary" (click)="openEdit(entry)">
              Edit
            </button>
            <button
              mat-icon-button
              color="primary"
              class="calendar-entry__complete-toggle"
              [ngClass]="{ 'calendar-entry__complete-toggle--checked': entry.status === CalendarEntryStatus.Completed }"
              (click)="toggleCompletion(entry)"
              [attr.aria-label]="entry.status === CalendarEntryStatus.Completed ? 'Mark as not done' : 'Mark as done'"
            >
              <mat-icon>
                {{ entry.status === CalendarEntryStatus.Completed ? 'check_circle' : 'radio_button_unchecked' }}
              </mat-icon>
            </button>
            <button
              mat-stroked-button
              color="accent"
              *ngIf="isPast(entry) && entry.status !== CalendarEntryStatus.Completed"
              (click)="markComplete(entry)"
            >
              <mat-icon>check</mat-icon>
              Mark done
            </button>
          </div>
        </div>
        <div class="calendar-entry__meta">
        </div>
      </div>
    </article>
  </section>

  <!-- Next 14 days shown in day slots -->
  <section class="calendar__14days" *ngIf="daySlots.length">
    <div
      class="day-slot"
      *ngFor="let slot of daySlots"
      [ngClass]="[
        'day-slot--' + slot.dayClass,
        slot.isToday ? 'day-slot--today' : ''
      ]"
    >
      <div
        class="day-slot__header"
        [ngClass]="[
          'day-slot__header--' + slot.dayClass,
          slot.isToday ? 'day-slot__header--today' : ''
        ]"
      >
        <div class="calendar-entry__date">
          <ng-container *ngIf="slot.isToday; else normalDate">Today</ng-container>
          <ng-template #normalDate>
            {{ slot.date | date: 'EEEE' }} {{ slot.date | date: 'dd MMM' }}
          </ng-template>
        </div>
      </div>

      <ng-container *ngIf="slot.entries.length; else emptyDay">
        <div class="day-slot__entries">
          <article class="calendar-entry calendar-entry--mini" *ngFor="let entry of slot.entries" [style.opacity]="entryOpacity(entry)">
            <div class="calendar-entry__body">
              <div class="calendar-entry__title-row">
                <div class="calendar-entry__title">
                  <h3>{{ entry.title }}</h3>
                  <div class="calendar-entry__subtitle" *ngIf="entry.subtitle">{{ entry.subtitle }}</div>
                </div>
                <div class="calendar-entry__actions">
                  <button mat-stroked-button color="primary" (click)="openReschedule(entry)">
                    Reschedule
                  </button>
                  <button mat-stroked-button color="primary" (click)="openEdit(entry)">
                    Edit
                  </button>
                  <button
                    mat-icon-button
                    color="primary"
                    class="calendar-entry__complete-toggle"
                    [ngClass]="{ 'calendar-entry__complete-toggle--checked': entry.status === CalendarEntryStatus.Completed }"
                    (click)="toggleCompletion(entry)"
                    [attr.aria-label]="entry.status === CalendarEntryStatus.Completed ? 'Mark as not done' : 'Mark as done'"
                  >
                    <mat-icon>
                      {{ entry.status === CalendarEntryStatus.Completed ? 'check_circle' : 'radio_button_unchecked' }}
                    </mat-icon>
                  </button>
                  <button
                    mat-stroked-button
                    color="accent"
                    *ngIf="isPast(entry) && entry.status !== CalendarEntryStatus.Completed"
                    (click)="markComplete(entry)"
                  >
                    <mat-icon>check</mat-icon>
                    Mark done
                  </button>
                </div>
              </div>
            </div>
          </article>
        </div>
      </ng-container>
      <ng-template #emptyDay>
        <ng-container *ngIf="slot.isToday">
          <div class="day-slot__empty day-slot__empty--today">
            <div class="day-slot__empty-title">No entries for today yet.</div>
            <div class="day-slot__empty-subtitle">Add a new entry to get started.</div>
          </div>
        </ng-container>
      </ng-template>
    </div>
  </section>

  <!-- Beyond 14 days. Entries are just listed -->
  <section class="calendar__list" *ngIf="beyondEntries.length">
    <article class="calendar-entry" *ngFor="let entry of beyondEntries" [style.opacity]="entryOpacity(entry)">
      <div class="calendar-entry__time">
        <div class="calendar-entry__date">{{ weeksUntil(entry) }}</div>
        <div class="calendar-entry__countdown">{{ asDate(entry) | date: 'EEE d MMM yyyy' }}</div>
      </div>
      <div class="calendar-entry__body">
        <div class="calendar-entry__title-row">
          <div class="calendar-entry__title">
            <h3>{{ entry.title }}</h3>
            <div class="calendar-entry__subtitle" *ngIf="entry.subtitle">{{ entry.subtitle }}</div>
          </div>
          <div class="calendar-entry__actions">
            <button mat-stroked-button color="primary" (click)="openReschedule(entry)">
              Reschedule
            </button>
            <button mat-stroked-button color="primary" (click)="openEdit(entry)">
              Edit
            </button>
            <button
              mat-icon-button
              color="primary"
              class="calendar-entry__complete-toggle"
              [ngClass]="{ 'calendar-entry__complete-toggle--checked': entry.status === CalendarEntryStatus.Completed }"
              (click)="toggleCompletion(entry)"
              [attr.aria-label]="entry.status === CalendarEntryStatus.Completed ? 'Mark as not done' : 'Mark as done'"
            >
              <mat-icon>
                {{ entry.status === CalendarEntryStatus.Completed ? 'check_circle' : 'radio_button_unchecked' }}
              </mat-icon>
            </button>
            <button
              mat-stroked-button
              color="accent"
              *ngIf="isPast(entry) && entry.status !== CalendarEntryStatus.Completed"
              (click)="markComplete(entry)"
            >
              <mat-icon>check</mat-icon>
              Mark done
            </button>
          </div>
        </div>
        <div class="calendar-entry__meta">
        </div>
      </div>
    </article>
  </section>

  <ng-template #emptyState>
    <div class="calendar__empty">
      Nothing scheduled yet. Add your first reminder to get started.
    </div>
  </ng-template>
</div>
